import {
	getSyncConfig,
	updateSyncConfig,
	type IntegrationSyncConfig,
	type IntegrationCapability,
	type DataTypeSyncConfig,
	type SyncDirection,
	type ConflictResolution,
} from "@/services/integration-management-service";
import {
	ArrowLeftOutlined,
	ArrowRightOutlined,
	CheckCircleOutlined,
	ClockCircleOutlined,
	ExclamationCircleOutlined,
	InfoCircleOutlined,
	SwapOutlined,
	StopOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Divider,
	Modal,
	Select,
	Spin,
	Switch,
	Tag,
	Tooltip,
	Typography,
	message,
} from "antd";
import { useEffect, useState } from "react";
import "./SyncConfigModal.scss";

const { Text, Title } = Typography;

interface SyncConfigModalProps {
	open: boolean;
	onClose: () => void;
	integrationId: number;
	integrationName: string;
}

const DIRECTION_OPTIONS: { value: SyncDirection; label: string; icon: React.ReactNode; color: string; description: string }[] = [
	{ value: "disabled", label: "Disabled", icon: <StopOutlined />, color: "default", description: "No data sync" },
	{ value: "push", label: "Push", icon: <ArrowRightOutlined />, color: "blue", description: "FacilityAI → External" },
	{ value: "pull", label: "Pull", icon: <ArrowLeftOutlined />, color: "green", description: "External → FacilityAI" },
	{ value: "bidirectional", label: "Bi-directional", icon: <SwapOutlined />, color: "orange", description: "Both directions" },
];

const CONFLICT_OPTIONS: { value: ConflictResolution; label: string; description: string }[] = [
	{ value: "manual", label: "Manual Review", description: "Flag conflicts for manual resolution (safest)" },
	{ value: "facilityai_wins", label: "FacilityAI Wins", description: "FacilityAI data takes priority" },
	{ value: "external_wins", label: "External Wins", description: "External CMMS data takes priority" },
	{ value: "newest_wins", label: "Newest Wins", description: "Most recently modified record wins" },
];

const SyncConfigModal = ({ open, onClose, integrationId, integrationName }: SyncConfigModalProps) => {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [syncConfig, setSyncConfig] = useState<IntegrationSyncConfig | null>(null);
	const [capabilities, setCapabilities] = useState<IntegrationCapability[]>([]);
	const [hasChanges, setHasChanges] = useState(false);

	useEffect(() => {
		if (open && integrationId) {
			loadSyncConfig();
		}
	}, [open, integrationId]);

	const loadSyncConfig = async () => {
		try {
			setLoading(true);
			const data = await getSyncConfig(integrationId);
			setSyncConfig(data.syncConfig);
			setCapabilities(data.capabilities);
			setHasChanges(false);
		} catch (error: any) {
			message.error(error.message || "Failed to load sync configuration");
		} finally {
			setLoading(false);
		}
	};

	const updateDataType = (dataType: string, updates: Partial<DataTypeSyncConfig>) => {
		if (!syncConfig) return;

		const updatedDataTypes = syncConfig.dataTypes.map((dt) => {
			if (dt.dataType === dataType) {
				const updated = { ...dt, ...updates };
				// When direction is set to something other than disabled, enable it
				if (updates.direction && updates.direction !== "disabled") {
					updated.isEnabled = true;
				} else if (updates.direction === "disabled") {
					updated.isEnabled = false;
				}
				return updated;
			}
			return dt;
		});

		setSyncConfig({ ...syncConfig, dataTypes: updatedDataTypes });
		setHasChanges(true);
	};

	const updateGlobalSetting = (key: keyof IntegrationSyncConfig, value: any) => {
		if (!syncConfig) return;
		setSyncConfig({ ...syncConfig, [key]: value });
		setHasChanges(true);
	};

	const handleSave = async () => {
		if (!syncConfig) return;

		try {
			setSaving(true);
			await updateSyncConfig(integrationId, syncConfig);
			message.success("Sync configuration saved");
			setHasChanges(false);
			onClose();
		} catch (error: any) {
			message.error(error.message || "Failed to save sync configuration");
		} finally {
			setSaving(false);
		}
	};

	const getCapability = (dataType: string): IntegrationCapability | undefined => {
		return capabilities.find((c) => c.dataType === dataType);
	};

	const getDirectionTag = (direction: SyncDirection) => {
		const opt = DIRECTION_OPTIONS.find((d) => d.value === direction);
		if (!opt) return null;
		return (
			<Tag color={opt.color} icon={opt.icon}>
				{opt.label}
			</Tag>
		);
	};

	const anyBidirectional = syncConfig?.dataTypes.some((dt) => dt.direction === "bidirectional") || false;

	return (
		<Modal
			title={
				<div className="sync-config-modal-title">
					<SwapOutlined style={{ marginRight: 8, color: "#92278F" }} />
					{integrationName} — Sync Configuration
				</div>
			}
			open={open}
			onCancel={onClose}
			onOk={handleSave}
			okText="Save Configuration"
			okButtonProps={{
				disabled: !hasChanges,
				loading: saving,
				style: { background: "#92278F", borderColor: "#92278F", color: "#fff" },
			}}
			width={720}
			destroyOnClose
		>
			{loading ? (
				<div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
					<Spin size="large" />
				</div>
			) : !syncConfig ? (
				<Alert type="warning" message="Unable to load sync configuration" />
			) : (
				<div className="sync-config-body">
					{/* Safety Warning */}
					<Alert
						type="info"
						showIcon
						icon={<InfoCircleOutlined />}
						message="Sync Direction Controls"
						description="Each data type has its own direction setting. Everything starts disabled — nothing syncs until you explicitly turn it on. For bidirectional sync, configure the conflict resolution to control which system wins."
						style={{ marginBottom: 20 }}
					/>

					{/* Data Type Direction Controls */}
					<div className="sync-data-types">
						<Title level={5} style={{ marginBottom: 16 }}>Data Types</Title>

						{syncConfig.dataTypes.map((dt) => {
							const capability = getCapability(dt.dataType);
							if (!capability) return null;

							return (
								<div key={dt.dataType} className={`sync-data-row ${dt.direction !== "disabled" ? "active" : ""}`}>
									<div className="sync-data-info">
										<div className="sync-data-label">
											<Text strong>{capability.label}</Text>
											{!capability.implemented && (
												<Tooltip title="Sync logic not yet implemented — you can pre-configure the direction">
													<Tag color="gold" style={{ marginLeft: 8, fontSize: 10 }}>Coming Soon</Tag>
												</Tooltip>
											)}
										</div>
										<Text type="secondary" className="sync-data-desc">
											{capability.description}
										</Text>
									</div>

									<div className="sync-data-controls">
										<Select
											value={dt.direction}
											onChange={(value: SyncDirection) => updateDataType(dt.dataType, { direction: value })}
											style={{ width: 160 }}
											size="small"
										>
											{DIRECTION_OPTIONS
												.filter((opt) => capability.supportedDirections.includes(opt.value))
												.map((opt) => (
													<Select.Option key={opt.value} value={opt.value}>
														<span style={{ display: "flex", alignItems: "center", gap: 6 }}>
															{opt.icon} {opt.label}
														</span>
													</Select.Option>
												))}
										</Select>

										{/* Conflict resolution for bidirectional */}
										{dt.direction === "bidirectional" && (
											<Select
												value={dt.conflictResolution}
												onChange={(value: ConflictResolution) => updateDataType(dt.dataType, { conflictResolution: value })}
												style={{ width: 160 }}
												size="small"
												placeholder="Conflict rule"
											>
												{CONFLICT_OPTIONS.map((opt) => (
													<Select.Option key={opt.value} value={opt.value}>
														<Tooltip title={opt.description} placement="left">
															{opt.label}
														</Tooltip>
													</Select.Option>
												))}
											</Select>
										)}
									</div>

									{/* Last synced indicator */}
									{dt.lastSyncedAt && (
										<div className="sync-data-last">
											<ClockCircleOutlined style={{ marginRight: 4, fontSize: 11 }} />
											<Text type="secondary" style={{ fontSize: 11 }}>
												Last: {new Date(dt.lastSyncedAt).toLocaleDateString()}
											</Text>
										</div>
									)}
								</div>
							);
						})}
					</div>

					<Divider />

					{/* Safety Settings */}
					<div className="sync-safety-settings">
						<Title level={5} style={{ marginBottom: 16 }}>Safety Settings</Title>

						<div className="safety-row">
							<div className="safety-info">
								<Text strong>Dry Run Mode</Text>
								<Text type="secondary" className="safety-desc">
									Preview what would change without actually applying anything
								</Text>
							</div>
							<Switch
								checked={syncConfig.dryRunMode}
								onChange={(checked) => updateGlobalSetting("dryRunMode", checked)}
								checkedChildren="On"
								unCheckedChildren="Off"
							/>
						</div>

						<div className="safety-row">
							<div className="safety-info">
								<Text strong>Require Approval</Text>
								<Text type="secondary" className="safety-desc">
									Changes must be manually approved before applying
								</Text>
							</div>
							<Switch
								checked={syncConfig.requireApproval}
								onChange={(checked) => updateGlobalSetting("requireApproval", checked)}
								checkedChildren="On"
								unCheckedChildren="Off"
							/>
						</div>

						<div className="safety-row">
							<div className="safety-info">
								<Text strong>Audit Log</Text>
								<Text type="secondary" className="safety-desc">
									Record all sync operations for review and compliance
								</Text>
							</div>
							<Switch
								checked={syncConfig.auditLogEnabled}
								onChange={(checked) => updateGlobalSetting("auditLogEnabled", checked)}
								checkedChildren="On"
								unCheckedChildren="Off"
							/>
						</div>

						<div className="safety-row">
							<div className="safety-info">
								<Text strong>Sync Schedule</Text>
								<Text type="secondary" className="safety-desc">
									How often sync runs automatically
								</Text>
							</div>
							<Select
								value={syncConfig.schedule}
								onChange={(value) => updateGlobalSetting("schedule", value)}
								style={{ width: 140 }}
								size="small"
							>
								<Select.Option value="manual">Manual Only</Select.Option>
								<Select.Option value="hourly">Every Hour</Select.Option>
								<Select.Option value="daily">Daily</Select.Option>
								<Select.Option value="weekly">Weekly</Select.Option>
							</Select>
						</div>

						<div className="safety-row">
							<div className="safety-info">
								<Text strong>Batch Size</Text>
								<Text type="secondary" className="safety-desc">
									Maximum records processed per sync run
								</Text>
							</div>
							<Select
								value={syncConfig.batchSize}
								onChange={(value) => updateGlobalSetting("batchSize", value)}
								style={{ width: 100 }}
								size="small"
							>
								<Select.Option value={25}>25</Select.Option>
								<Select.Option value={50}>50</Select.Option>
								<Select.Option value={100}>100</Select.Option>
								<Select.Option value={250}>250</Select.Option>
								<Select.Option value={500}>500</Select.Option>
							</Select>
						</div>
					</div>

					{/* Warning for bidirectional */}
					{anyBidirectional && (
						<Alert
							type="warning"
							showIcon
							icon={<ExclamationCircleOutlined />}
							message="Bidirectional Sync Active"
							description="You have bidirectional sync enabled for one or more data types. Make sure conflict resolution is configured correctly to prevent data loss. We recommend starting with 'Manual Review' and Dry Run mode on."
							style={{ marginTop: 16 }}
						/>
					)}
				</div>
			)}
		</Modal>
	);
};

export { SyncConfigModal };
